import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { Button, COLOR_SYSTEM, Text, Spinner } from 'components/atoms';
import Dropdown, { DropdownMenuItem } from 'components/molecules/Dropdown';
import { StepData } from './index';
import { promptService } from 'api/promptService';
import { PromptType, PROMPT_TYPE_KOREAN_NAMES, RequestPresets, PERMISSION_DESCRIPTIONS, AVAILABLE_AI_MODELS } from 'common/models/types';

interface ExperimentStep3Props {
  data: StepData;
  onNext: (data: Partial<StepData>) => void;
  onBack: () => void;
}

// DOC_NAME (문서 타입)
const DOC_NAME: Record<string, string> = {
  BEERS2023: "BEER's",
  SPSTv3: "SPST",
  SPSTv2: "SPST",
  CCMM: "통합약물관리",
  LCMM: "요양시설가이드라인",
  POLY: "다제",
  KABS: "KABS",
};

const ExperimentStep3: React.FC<ExperimentStep3Props> = ({
  data,
  onNext,
  onBack,
}) => {
  // RequestPresets에서 기본값 가져오기
  const presets = RequestPresets[data.promptType || ''] || RequestPresets.default;
  
  const [formData, setFormData] = useState({
    promptType: data.promptType || '',
    modelType: data.modelType || 'GPT_4O',
    promptId: data.promptId,
    medicationOptions: data.medicationOptions || presets.medicationOptions || [],
    docsOptions: data.docsOptions || presets.docsOptions || [],
  });
  
  const [showPromptTypeDropdown, setShowPromptTypeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  
  const promptTypeOptions = [
    { value: PromptType.MEDICATION_COUNSELING, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.MEDICATION_COUNSELING] },
    { value: PromptType.CMM_2, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.CMM_2] },
    { value: PromptType.LCMM, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.LCMM] },
    { value: PromptType.FREE_QUESTION, label: PROMPT_TYPE_KOREAN_NAMES[PromptType.FREE_QUESTION] },
  ];
  
  // 프롬프트 목록 조회
  const { data: promptsResponse, isLoading: isLoadingPrompts } = useQuery({
    queryKey: ['prompts', formData.promptType],
    queryFn: () => promptService.getPrompts({
      promptType: formData.promptType,
    }),
    enabled: !!formData.promptType,
  });
  
  const prompts = promptsResponse?.data || [];
  
  // promptType 변경 시 RequestPresets 기본값 적용 및 프롬프트 초기화
  useEffect(() => {
    if (formData.promptType) {
      const presets = RequestPresets[formData.promptType] || RequestPresets.default;
      setFormData(prev => ({
        ...prev,
        promptId: undefined, // 프롬프트 타입 변경 시 프롬프트 선택 초기화
        medicationOptions: presets.medicationOptions || [],
        docsOptions: presets.docsOptions || [],
      }));
    }
  }, [formData.promptType]);

  // 프롬프트 선택 시 모델 기본값 설정
  useEffect(() => {
    const selectedPrompt = prompts.find(p => p.id === formData.promptId);
    if (selectedPrompt?.model) {
      setFormData(prev => ({ ...prev, modelType: selectedPrompt.model }));
    }
  }, [formData.promptId, prompts]);
  
  // 모드 1이 선택되지 않았으면 다음 단계로
  useEffect(() => {
    if (!data.modes?.createMode) {
      onNext({});
    }
  }, [data.modes?.createMode]);
  
  const handleNext = () => {
    onNext({
      promptType: formData.promptType as PromptType,
      modelType: formData.modelType,
      promptId: formData.promptId,
      medicationOptions: formData.medicationOptions,
      docsOptions: formData.docsOptions,
    });
  };
  
  const toggleMedicationOption = (key: string) => {
    setFormData(prev => ({
      ...prev,
      medicationOptions: prev.medicationOptions.includes(key)
        ? prev.medicationOptions.filter(opt => opt !== key)
        : [...prev.medicationOptions, key]
    }));
  };
  
  const toggleDocOption = (key: string) => {
    setFormData(prev => ({
      ...prev,
      docsOptions: prev.docsOptions.includes(key)
        ? prev.docsOptions.filter(opt => opt !== key)
        : [...prev.docsOptions, key]
    }));
  };
  
  // 모드 1이 선택되지 않았으면 렌더링하지 않음
  if (!data.modes?.createMode) {
    return null;
  }
  
  return (
    <Container>
      <StepTitle>AI Review 생성 설정</StepTitle>
      <StepDescription>
        AI Review 생성에 필요한 프롬프트와 옵션을 설정하세요.
      </StepDescription>
      
      <FormGroup>
        <Label>프롬프트 타입 <Required>*</Required></Label>
        <Dropdown
          defaultValue="프롬프트 타입을 선택하세요"
          selected={
            promptTypeOptions.find(opt => opt.value === formData.promptType)?.label || 
            '프롬프트 타입을 선택하세요'
          }
          size="Medium"
          active={showPromptTypeDropdown}
          onClick={() => setShowPromptTypeDropdown(!showPromptTypeDropdown)}
        >
          {promptTypeOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, promptType: option.value });
                setShowPromptTypeDropdown(false);
              }}
              isSelected={formData.promptType === option.value}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </Dropdown>
      </FormGroup>
      
      <FormGroup>
        <Label>프롬프트 선택 {formData.promptType && <Required>*</Required>}</Label>
        {!formData.promptType ? (
          <DisabledDropdown>먼저 프롬프트 타입을 선택하세요</DisabledDropdown>
        ) : isLoadingPrompts ? (
          <Spinner size="Small" />
        ) : (
          <Dropdown
            defaultValue="프롬프트를 선택하세요"
            selected={
              prompts.find(p => p.id === formData.promptId) 
                ? `ID: ${formData.promptId} - ${prompts.find(p => p.id === formData.promptId)?.version}`
                : '프롬프트를 선택하세요'
            }
            size="Medium"
            active={showPromptDropdown}
            onClick={() => setShowPromptDropdown(!showPromptDropdown)}
          >
            {prompts.map((prompt) => (
              <DropdownMenuItem
                key={prompt.id}
                onClick={() => {
                  setFormData({ ...formData, promptId: prompt.id });
                  setShowPromptDropdown(false);
                }}
                isSelected={formData.promptId === prompt.id}
              >
                ID: {prompt.id} - {prompt.version} {prompt.description && `(${prompt.description})`}
              </DropdownMenuItem>
            ))}
          </Dropdown>
        )}
      </FormGroup>
      
      <FormGroup>
        <Label>모델 선택</Label>
        <Dropdown
          defaultValue="모델을 선택하세요"
          selected={formData.modelType}
          size="Medium"
          active={showModelDropdown}
          onClick={() => setShowModelDropdown(!showModelDropdown)}
        >
          {AVAILABLE_AI_MODELS.map((model) => (
            <DropdownMenuItem
              key={model}
              onClick={() => {
                setFormData({ ...formData, modelType: model });
                setShowModelDropdown(false);
              }}
              isSelected={formData.modelType === model}
            >
              {model}
            </DropdownMenuItem>
          ))}
        </Dropdown>
        {formData.promptId && (
          <HelperText>선택한 프롬프트의 기본 모델이 자동으로 설정되었습니다.</HelperText>
        )}
      </FormGroup>
      
      <FormGroup>
        <Label>약물 정보 옵션</Label>
        <OptionsGrid>
          {Object.entries(PERMISSION_DESCRIPTIONS).map(([key, value]) => (
            <OptionItem key={key}>
              <Checkbox
                type="checkbox"
                id={`med-${key}`}
                checked={formData.medicationOptions.includes(key)}
                onChange={() => toggleMedicationOption(key)}
              />
              <OptionLabel htmlFor={`med-${key}`}>
                {value}
              </OptionLabel>
            </OptionItem>
          ))}
        </OptionsGrid>
        <HelperText>프롬프트 타입에 따른 권장 옵션이 자동으로 선택되었습니다.</HelperText>
      </FormGroup>
      
      <FormGroup>
        <Label>문서 옵션</Label>
        <OptionsGrid>
          {Object.entries(DOC_NAME).map(([key, value]) => (
            <OptionItem key={key}>
              <Checkbox
                type="checkbox"
                id={`doc-${key}`}
                checked={formData.docsOptions.includes(key)}
                onChange={() => toggleDocOption(key)}
              />
              <OptionLabel htmlFor={`doc-${key}`}>
                {value}
              </OptionLabel>
            </OptionItem>
          ))}
        </OptionsGrid>
        <HelperText>프롬프트 타입에 따른 권장 문서가 자동으로 선택되었습니다.</HelperText>
      </FormGroup>
      
      <ButtonGroup>
        <Button
          size="Medium"
          type="Text"
          variant="Ghost"
          color="Secondary"
          onClick={onBack}
        >
          이전
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleNext}
        >
          다음
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const StepDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 16px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const OptionLabel = styled.label`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  cursor: pointer;
  user-select: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

const Required = styled.span`
  color: ${COLOR_SYSTEM.get("Red")?.[500]};
  margin-left: 4px;
`;

const DisabledDropdown = styled.div`
  padding: 10px 16px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 8px;
  font-size: 14px;
  cursor: not-allowed;
`;

export default ExperimentStep3;